// main imports
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Tab, Tabs } from "react-bootstrap";

// backend
import { waitForManufacturingAsync } from "@/lib/businessLogic";
import { getBackend } from "@/lib/backend/backend2";
import type { IGetBlobStorageItemAsStringOrNullRes } from "@/lib/backend/common.generated.types";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { LINE_TAGS_ACCESS, LINE_TYPES, PLANT_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { niceNumber, renderTag } from "@/lib/util";
import * as Auth from "@/lib/auth";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ShiftEditorWeek from "@/modules/shared/planningTable/ShiftEditorWeek";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Select from "react-select";
import Toggle from "react-toggle";
import LinesDetailsSaveCalendarLineModal from "./LinesDetailsSaveCalendarLineModal";

import type { State as LineCreateState } from "./LineCreate";
import LinesCreateEditCompetenceModal from "./LinesCreateEditCompetenceModal";
import LinesCompetentPeople from "./LinesCompetentPeople";
import { filterLineLevelCompetences } from "./lineCompetenceUtils";
import {
    buildPersonActiveByUuid,
    countCompetentPeopleActive,
    filterCompetencesByOnlyActive,
    uniqueCompetentPersonUuids,
} from "./lineCompetentPeopleActiveUtils";
import ConfirmationModal from "@/modules/shared/ui/ConfirmationModal";
import * as BusinessLogic from "@/lib/businessLogic";
import Loader from "@/modules/shared/ui/Loader";
import LineStatusDetails from "@/modules/shared/lineStatus/LineStatusDetails";

import "./LinesDetails.scss";

type Props = {
    selected: t.ILineData;
    editLink: string;
    kanbanLink: string;
    handleToDelete: (uuid: string) => void;
    reloadComponent: () => void;
};

export type CompetenceShortData = {
    id: number;
    title: string;
    factor: number;
    weight: number;
    line_uuid: string;
};

type CalendarLineOption = {
    value: string;
    label: string;
};

type DetailsTab = "status" | "competent_people" | "ekanban";

type State = {
    lineGroups: t.ILineGroupData[];
    kanban: t.IKanbanLine;
    status: t.ILineStatusItem | null;
    calendar_line: string; // to show grid of shifts
    calendar_line_options: CalendarLineOption[]; // to show dropdown of calendar line options
    message: {
        msg: string;
        type: "error" | "warning" | "success" | "info" | "no-data";
    };
    // if we dont have specific calendar line for this line, we use the one from plant
    calendar_line_plant: string;
    manual_calendar_line_edit: boolean; // for edit toggle
    show_save_shift_modal: boolean; // for modal
    should_update_calendar_line_options: boolean;
    selected_line_tags: t.ITags;
    // if this line just copy calendar line from other line, we dont show edit button
    show_button_edit_calendar_line: boolean;
    child_competences: CompetenceShortData[];
    selected_competence: CompetenceShortData | null;
    show_create_edit_competence_modal: boolean;
    show_delete_competence_modal: boolean;
    loading_child_lines: boolean;
    line_competences: t.ILineCompetenceData[];
    loading_line_competences: boolean;
    line_competences_error: string;
    line_competences_active_warning: string;
    person_active_by_uuid: Map<string, boolean>;
    person_active_enrichment_failed: boolean;
    only_active_competent_people: boolean;
    categories: t.ICategory[];
    active_tab: DetailsTab;
};

const calendar_line_options_filename = "calendar_line_options.json";

/**
 * Lines details component.
 */
class LinesDetails extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const state: State = {
            lineGroups: [],
            status: null,
            kanban: {
                enabled: false,
                line_title: props.selected.title,
                line_uuid: props.selected.uuid,
                materials: [],
            },
            calendar_line: LINE_TAGS_ACCESS.calendar_line(this.props.selected.tags),
            calendar_line_options: [],
            message: {
                msg: "",
                type: "info",
            },
            calendar_line_plant: "",
            manual_calendar_line_edit: !!LINE_TAGS_ACCESS.calendar_line(this.props.selected.tags),
            show_save_shift_modal: false,
            should_update_calendar_line_options: true,
            selected_line_tags: this.props.selected.tags,
            show_button_edit_calendar_line: LINE_TAGS_ACCESS.copy_calendar_line_from(this.props.selected.tags) === "",
            child_competences: [],
            selected_competence: null,
            show_create_edit_competence_modal: false,
            show_delete_competence_modal: false,
            loading_child_lines: false,
            line_competences: [],
            loading_line_competences: false,
            line_competences_error: "",
            line_competences_active_warning: "",
            person_active_by_uuid: new Map(),
            person_active_enrichment_failed: false,
            only_active_competent_people: true,
            categories: [],
            active_tab: "status",
        };
        this.state = state;
    }

    async componentDidMount() {
        // update manufacturing meta-data
        await this.loadLineGroups();

        if (this.hasEnabledChildLines()) {
            // get all child lines
            await this.loadChildLines();
        }
        // get people with line-level competence on the selected line
        await this.loadLineCompetences();
        // get kanban data for the selected line
        await this.loadKanban();
        // create calendar line options if they do not exist
        await this.createCalendarLineOptionsFile();
        // get castegories for the selected line
        await this.loadCategories();
        // get status for the selected line
        await this.loadLineStatus();
    }

    async componentDidUpdate(prevProps: Props) {
        // do nothing if not selected
        if (!this.props.selected) {
            return;
        }

        const calendar_line: string = LINE_TAGS_ACCESS.calendar_line(this.props.selected.tags);

        // check if the selected line changed
        if (
            !prevProps.selected ||
            this.props.selected.uuid !== prevProps.selected.uuid ||
            this.props.selected.id !== prevProps.selected.id
        ) {
            this.setState({
                loading_line_competences: true,
                line_competences: [],
                line_competences_error: "",
                line_competences_active_warning: "",
                person_active_by_uuid: new Map(),
                person_active_enrichment_failed: false,
            });
            // update manufacturing meta-data
            await this.loadLineGroups();

            if (this.hasEnabledChildLines()) {
                // get all child lines
                await this.loadChildLines();
            }
            // get people with line-level competence on the selected line
            await this.loadLineCompetences();
            // get kanban data for the selected line
            await this.loadKanban();
            await this.loadLineStatus();

            const manual_calendar_line_edit = !!LINE_TAGS_ACCESS.calendar_line(this.props.selected.tags);
            const show_button_edit_calendar_line =
                LINE_TAGS_ACCESS.copy_calendar_line_from(this.props.selected.tags) === "";
            const selected_line_tags = this.props.selected.tags;

            this.setState({
                manual_calendar_line_edit,
                message: { msg: "", type: "info" },
                selected_line_tags,
                show_button_edit_calendar_line,
            });

            // get categories for the selected line
            await this.loadCategories();
        }
        // check if calendar line changed
        if (calendar_line !== LINE_TAGS_ACCESS.calendar_line(prevProps.selected.tags)) {
            this.setState({ calendar_line });
        }

        // get calendar line options from blob storage
        let calendar_line_options_blob: IGetBlobStorageItemAsStringOrNullRes = { content: "" };
        if (this.state.should_update_calendar_line_options) {
            try {
                calendar_line_options_blob = await getBackend().common.getBlobStorageItemAsStringOrNull({
                    key: calendar_line_options_filename,
                });
                this.setState({ should_update_calendar_line_options: false });
                this.setState({ message: { msg: "", type: "no-data" } });
            } catch (error) {
                this.setState({
                    message: { msg: translate("common.error", "An error occured while fetching data"), type: "error" },
                });
            }
        }

        let parsed_calendar_line_options_blob: CalendarLineOption[] | void = undefined;

        // Check if blob storage file is in JSON format
        try {
            if (calendar_line_options_blob.content !== null && calendar_line_options_blob.content !== "") {
                parsed_calendar_line_options_blob = JSON.parse(calendar_line_options_blob.content);
            }
        } catch (e) {
            this.setState({
                message: { msg: (e as Error).message, type: "error" },
            });
        }

        // if calendar line options are not loaded yet and blob storage has content, load them
        if (
            this.state.calendar_line_options !== null &&
            this.state.calendar_line_options.length === 0 &&
            parsed_calendar_line_options_blob !== undefined
        ) {
            this.setState({ calendar_line_options: parsed_calendar_line_options_blob });
        }

        if (this.state.calendar_line_plant === "") {
            await this.getPlantCalendarLine();
        }

        // if calendar line is empty, set it to plant calendar line
        if (this.state.calendar_line === "") {
            this.setState({ calendar_line: this.state.calendar_line_plant });
        }
    }

    hasEnabledChildLines() {
        const plant_tags = BusinessLogic.getPlantTags(this.props.selected.plant_uuid);
        const line_type = LINE_TAGS_ACCESS.type(this.props.selected.tags);
        return PLANT_TAGS_ACCESS.enable_child_lines_in_digital_twin(plant_tags) && line_type === LINE_TYPES.ordinary;
    }

    async createCalendarLineOptionsFile() {
        // get calendar line options from blob storage
        let calendar_line_options_blob: IGetBlobStorageItemAsStringOrNullRes = { content: null };
        try {
            calendar_line_options_blob = await getBackend().common.getBlobStorageItemAsStringOrNull({
                key: calendar_line_options_filename,
            });
            this.setState({ message: { msg: "", type: "no-data" } });
        } catch (error) {
            this.setState({
                message: { msg: translate("common.error", "An error occured while fetching data"), type: "error" },
            });
        }

        if (calendar_line_options_blob.content === null) {
            try {
                // if blob storage item does not exist, create it
                await getBackend().common.setBlobStorageItemAsString({
                    key: calendar_line_options_filename,
                    content: JSON.stringify([]),
                });
                this.setState({ message: { msg: "", type: "no-data" } });
            } catch (error) {
                this.setState({
                    message: {
                        msg: translate("common.error_save", "An error occured while saving data"),
                        type: "error",
                    },
                });
            }
        }
    }

    async loadLineGroups() {
        // update manufacturing meta-data
        await waitForManufacturingAsync();
        // get line groups for selected line
        const { line_groups } = await getBackend().manufacturing.getLineGroups({});
        const lineGroups = line_groups.filter(x => x.line_uuids.indexOf(this.props.selected.uuid) >= 0);
        this.setState({ lineGroups });
    }

    async loadChildLines() {
        this.setState({ loading_child_lines: true });
        try {
            const { line } = await getBackend().manufacturing.getLine({ line_uuid: this.props.selected.uuid });
            const child_lines = line.child_lines;
            if (child_lines.length === 0) {
                this.setState({ child_competences: [] });
                return;
            }

            const { competences } = await getBackend().manufacturing.getCompetences({});
            if (competences.length === 0) {
                this.setState({ child_competences: [] });
                return;
            }

            const competences_map: Map<number, t.ICompetenceData> = new Map(
                competences.map(competence => [
                    competence.id,
                    {
                        code: competence.code,
                        id: competence.id,
                        title: competence.title,
                    },
                ])
            );

            const child_competences: CompetenceShortData[] = [];
            for (const child_line of child_lines) {
                const competence_id = child_line.competence_id;
                if (competence_id === null) {
                    continue;
                }

                const competence = competences_map.get(competence_id);
                if (competence) {
                    child_competences.push({
                        id: competence.id,
                        title: competence.title,
                        factor: child_line.factor,
                        weight: child_line.weight,
                        line_uuid: child_line.uuid,
                    });
                }
            }

            this.setState({ child_competences });
        } catch (err) {
            this.setState({ child_competences: [] });
        } finally {
            this.setState({ loading_child_lines: false });
        }
    }

    async loadCategories() {
        try {
            const { categories } = await getBackend().manufacturing.getLineCategories({
                line_id: this.props.selected.id,
            });
            this.setState({ categories });
        } catch (err) {
            console.log(err);
        }
    }

    async loadLineCompetences() {
        const line_uuid = this.props.selected.uuid;
        this.setState({
            loading_line_competences: true,
            line_competences_error: "",
            line_competences_active_warning: "",
            person_active_by_uuid: new Map(),
            person_active_enrichment_failed: false,
        });
        try {
            const { competences } = await getBackend().manufacturing.getLineCompetence({
                line_uuid,
            });
            if (this.props.selected.uuid !== line_uuid) {
                return;
            }
            const line_competences = filterLineLevelCompetences(competences);
            let person_active_by_uuid = new Map<string, boolean>();
            let person_active_enrichment_failed = false;
            let line_competences_active_warning = "";

            const person_uuids = uniqueCompetentPersonUuids(line_competences);
            if (person_uuids.length > 0) {
                try {
                    const { people } = await getBackend().manufacturing.getPeople({
                        uuids: person_uuids,
                        include_inactive: true,
                    });
                    if (this.props.selected.uuid !== line_uuid) {
                        return;
                    }
                    person_active_by_uuid = buildPersonActiveByUuid(people);
                } catch (err) {
                    if (this.props.selected.uuid !== line_uuid) {
                        return;
                    }
                    person_active_enrichment_failed = true;
                    line_competences_active_warning =
                        (err as Error).message ||
                        translate(
                            "Manufacturing.Lines.competent_people_active_load_failed",
                            "Could not load whether competent people are active. Showing everyone as active."
                        );
                }
            }

            if (this.props.selected.uuid !== line_uuid) {
                return;
            }
            this.setState({
                line_competences,
                line_competences_error: "",
                person_active_by_uuid,
                person_active_enrichment_failed,
                line_competences_active_warning,
            });
        } catch (err) {
            if (this.props.selected.uuid !== line_uuid) {
                return;
            }
            this.setState({
                line_competences: [],
                line_competences_error:
                    (err as Error).message || translate("common.error", "An error occurred while fetching data"),
            });
        } finally {
            if (this.props.selected.uuid === line_uuid) {
                this.setState({ loading_line_competences: false });
            }
        }
    }

    getCompetentPeopleCounts = () => {
        return countCompetentPeopleActive(
            this.state.line_competences,
            this.state.person_active_by_uuid,
            this.state.person_active_enrichment_failed
        );
    };

    formatCompetentPeopleCountLabel = () => {
        if (this.state.loading_line_competences) {
            return "0 active, 0 total";
        }
        const { active_count, total_count } = this.getCompetentPeopleCounts();
        return translate("Manufacturing.Lines.competent_people_active_total_counts", "{activeCount} / {totalCount}")
            .replace("{activeCount}", String(active_count))
            .replace("{totalCount}", String(total_count));
    };

    toggleOnlyActiveCompetentPeople = () => {
        this.setState(prev => ({ only_active_competent_people: !prev.only_active_competent_people }));
    };

    async loadKanban() {
        try {
            const res = await getBackend().manufacturing.getLineKanban({ uuid: this.props.selected.uuid });
            this.setState({ kanban: res.data });
        } catch (err) {
            console.log(err);
        }
    }

    async loadLineStatus() {
        try {
            const { data } = await getBackend().manufacturing.getLineStatuses({
                lineUuids: [this.props.selected.uuid],
                includeStale: true,
            });
            this.setState({ status: data[0] ?? null });
        } catch (err) {
            console.log(err);
            this.setState({ status: null });
        }
    }

    /**
     * Handle delete button clicks.
     */
    handleDelete = () => {
        if (this.props.selected) {
            this.props.handleToDelete(this.props.selected.uuid);
        }
    };

    renderSaveCalendarLineModal() {
        return (
            <LinesDetailsSaveCalendarLineModal
                show={this.state.show_save_shift_modal}
                onHide={this.closeSaveShiftModal}
                calendar_line_options={this.state.calendar_line_options}
                calendar_line={this.state.calendar_line}
            />
        );
    }

    closeSaveShiftModal = () => {
        this.setState({ show_save_shift_modal: false, should_update_calendar_line_options: true });
    };

    renderMaterials() {
        return this.state.kanban.materials.map((material, i) => {
            return (
                <tr key={i}>
                    <td>{material.material_title}</td>
                    <td>{niceNumber(material.min_stock, 0)}</td>
                    <td>{niceNumber(material.max_stock, 0)}</td>
                </tr>
            );
        });
    }

    renderDashboardLinks() {
        return this.state.kanban && this.state.kanban.dashboard_uuid ? (
            <div className="button-list order-last ml-auto">
                <Link
                    className="btn btn-outline-secondary"
                    to={"/dashboard-public/" + this.state.kanban.dashboard_uuid}
                >
                    <FormattedMessage id="common.screen" defaultMessage="Screen" />
                </Link>
            </div>
        ) : (
            ""
        );
    }

    handleClickEnableShift(day: number, shift: number, active: boolean) {
        // if we are not in manual_mode, do nothing
        if (this.state.manual_calendar_line_edit === false || this.state.show_button_edit_calendar_line === false) {
            return;
        }

        // Split calendar line into array of characters (0 or 1)
        const new_calendar_line_array = this.state.calendar_line.split("");

        // Change the value of the selected shift
        new_calendar_line_array[day * 3 + shift] = active ? "0" : "1";

        // Join the array back into a string
        const new_calendar_line = new_calendar_line_array.join("");

        // Update the state
        this.setState({ calendar_line: new_calendar_line });
    }

    async saveCalendarLineToLineTags() {
        try {
            this.setState({ message: { msg: translate("common.please_wait", "Please wait..."), type: "info" } });
            await getBackend().manufacturing.updateLine({
                line_uuid: this.props.selected.uuid,
                title: this.props.selected.title,
                external_id: this.props.selected.external_id,
                weight: this.props.selected.weight,
                tags: {
                    ...this.state.selected_line_tags,
                    calendar_line: this.state.calendar_line,
                },
            });
            this.setState({ message: { msg: "", type: "no-data" } });
        } catch (error) {
            this.setState({
                message: { msg: translate("common.error_save", "An error occured while saving data"), type: "error" },
            });
        }

        this.setState({
            message: {
                msg: translate("Calendar_line.saved_this_schedule_to_line", "Saved this schedule to line"),
                type: "success",
            },
            selected_line_tags: {
                ...this.state.selected_line_tags,
                calendar_line: this.state.calendar_line,
            },
        });

        // reload parent component (Lines.tsx)
        this.props.reloadComponent();
    }

    async removeCalendarLineFromLineTags() {
        // get tags
        const tags = { ...this.state.selected_line_tags };

        // remove this tag for schedule from line tags
        delete tags.calendar_line;

        try {
            // send this action to backend
            await getBackend().manufacturing.updateLine({
                line_uuid: this.props.selected.uuid,
                title: this.props.selected.title,
                external_id: this.props.selected.external_id,
                weight: this.props.selected.weight,
                tags: tags,
            });

            // set calendar line to plant calendar line, because we removed it from line tags
            this.setState({
                message: {
                    msg: translate(
                        "Calendar_line.remove_custom_schedule",
                        "You removed tag from the line. This will copy schedule from plant calendar"
                    ),
                    type: "success",
                },
                // when removing line specific calendar line, set calendar line to plant calendar line
                calendar_line: this.state.calendar_line_plant,
                selected_line_tags: tags,
            });
        } catch (error) {
            this.setState({
                message: { msg: translate("common.error_save", "An error occured while saving data"), type: "error" },
            });
        }
    }

    async saveCalendarLineOption() {
        this.setState({ show_save_shift_modal: true });
    }

    async deleteCalendarLineOption() {
        const calendar_line_options = this.state.calendar_line_options;

        // remove this calendar line option
        const new_calendar_line_options = calendar_line_options.filter(
            option => option.value !== this.state.calendar_line
        );

        try {
            // save it to blob storage
            await getBackend().common.setBlobStorageItemAsString({
                key: calendar_line_options_filename,
                content: JSON.stringify(new_calendar_line_options),
            });

            // update state for calendar line options (dropdown)
            this.setState({
                calendar_line_options: new_calendar_line_options,
                message: {
                    msg: translate("Calendar_line.schedule_removed", "Schedule removed"),
                    type: "warning",
                },
            });
        } catch (error) {
            this.setState({
                message: { msg: translate("common.error", "An error occured while fetching data"), type: "error" },
            });
        }
    }

    async toggleManualCalendarLineEdit() {
        // if we are turning off manual calendar line edit, remove the calendar line from line tags
        if (this.state.manual_calendar_line_edit === true) {
            await this.removeCalendarLineFromLineTags();
        }

        // toggle state
        this.setState({ manual_calendar_line_edit: !this.state.manual_calendar_line_edit });
    }

    async getPlantCalendarLine() {
        const plant_uuid = this.props.selected.plant_uuid;

        try {
            const plants = await getBackend().manufacturing.getPlants({});
            const plant = plants.plants.find(plant => plant.uuid === plant_uuid);

            if (plant === undefined) {
                console.log("Plant not found");
            }

            let calendar_line_plant = "111111111111111111111";

            if (plant && plant.tags) {
                calendar_line_plant = PLANT_TAGS_ACCESS.calendar_plant(plant.tags);
            }

            this.setState({ calendar_line_plant, message: { msg: "", type: "no-data" } });
        } catch (error) {
            this.setState({
                message: { msg: translate("common.error", "An error occured while fetching data"), type: "error" },
            });
        }
    }

    toggleAllShifts = (shift: number) => {
        // if we are not in manual_mode, do nothing
        if (this.state.manual_calendar_line_edit === false || this.state.show_button_edit_calendar_line === false) {
            return;
        }
        // Split calendar line into array of characters (0 or 1)
        const new_calendar_line_array = this.state.calendar_line.split("");
        const should_enable = new_calendar_line_array[shift] === "0" ? "1" : "0";
        // Change the value of the selected shift
        for (let i = 0; i < 7; i++) {
            new_calendar_line_array[i * 3 + shift] = should_enable;
        }
        // Join the array back into a string
        const new_calendar_line = new_calendar_line_array.join("");
        // Update the state
        this.setState({ calendar_line: new_calendar_line });
    };

    renderLineShifts() {
        // First check if we have a calendar line for this line in the line tags
        let calendar_line = this.state.calendar_line;

        // If not, use the calendar line from the plant tags
        if (calendar_line === "" || calendar_line === undefined) {
            calendar_line = this.state.calendar_line_plant;
        }

        const shifts: t.IShiftLineRec[][] = [];

        // days (monday, tuesday, wednesday, thursday, friday, saturday, sunday)
        for (let i = 0; i < 7; i++) {
            const day_shifts: t.IShiftLineRec[] = [];

            // shifts (morning, afternoon, night)
            for (let j = 0; j < 3; j++) {
                day_shifts.push({
                    enabled: calendar_line[i * 3 + j] === "1" ? true : false,
                    shift_number: i * 3 + j,
                    line_uuid: "", // just to make the types happy
                    org_uuid: "", // just to make the types happy
                    shift_line_id: 0, // just to make the types happy
                    shift_uuid: "", // just to make the types happy
                    user_override: false, // just to make the types happy
                    week: 0, // just to make the types happy
                    year: 0, // just to make the types happy
                    shift_date: 0, // just to make the types happy
                });
            }
            shifts.push(day_shifts);
        }

        const calendarLineOptions: { label: string; selected: boolean; value: string }[] = [];
        let found_selected_option = false;
        // generate calendar line options for dropdown
        for (let i = 0; this.state.calendar_line_options !== null && i < this.state.calendar_line_options.length; i++) {
            const option = this.state.calendar_line_options[i];

            const selected = option.value === this.state.calendar_line;

            if (selected) {
                found_selected_option = true;
            }

            calendarLineOptions.push({
                value: option.value,
                label: option.label,
                selected: option.value === this.state.calendar_line,
            });
        }

        // if the selected calendar line is not in the options, add "Custom" to the end
        if (!found_selected_option) {
            calendarLineOptions.push({
                value: this.state.calendar_line,
                label: translate("Calendar_line.custom_schedule", "Custom schedule"),
                selected: true,
            });
        }

        return (
            <div className="d-flex flex-row align-items-end">
                {this.state.show_button_edit_calendar_line === true &&
                    this.state.manual_calendar_line_edit === true && (
                        <div className="action-buttons d-flex flex-column">
                            <button
                                className="schedule-btn btn btn-primary mb-1"
                                onClick={() => this.saveCalendarLineOption()}
                                disabled={
                                    (this.state.calendar_line_options !== null &&
                                        this.state.calendar_line_options.find(
                                            option => option.value === this.state.calendar_line
                                        )) !== undefined
                                }
                            >
                                {translate("Calendar_line.save_schedule_as", "Save schedule as ... ")}
                            </button>

                            <button
                                className="schedule-btn btn btn-primary mb-1"
                                onClick={() => this.deleteCalendarLineOption()}
                                disabled={
                                    (this.state.calendar_line_options !== null &&
                                        this.state.calendar_line_options.find(
                                            option => option.value === this.state.calendar_line
                                        )) === undefined
                                }
                            >
                                {translate("Calendar_line.remove_this_schedule", "Remove this schedule")}
                            </button>

                            <Select
                                className="form-select mb-1"
                                options={calendarLineOptions}
                                onChange={selectedOption => this.setState({ calendar_line: selectedOption.value })}
                                value={calendarLineOptions.find(option => option.value === this.state.calendar_line)}
                            />
                            {/* Button is disabled if value in tag is same as it is shown */}
                            <button
                                className="schedule-btn btn btn-primary mb-1"
                                onClick={() => this.saveCalendarLineToLineTags()}
                                disabled={
                                    LINE_TAGS_ACCESS.calendar_line(this.state.selected_line_tags) ===
                                    this.state.calendar_line
                                }
                            >
                                {translate("Calendar_line.save_this_schedule_to_line", "Save this schedule to line")}
                            </button>
                        </div>
                    )}
                <ShiftEditorWeek
                    current_shift={-1}
                    current_week={1}
                    key={"key"}
                    shifts={shifts}
                    handleClick={(day, shift, active) => this.handleClickEnableShift(shift, day, active)}
                    toggleAllShifts={shift => this.toggleAllShifts(shift)}
                    show_shift_names={true}
                    hide_tooltip={true}
                />
            </div>
        );
    }

    showDeleteCompetenceModal = (competence: CompetenceShortData) => {
        this.setState({ show_delete_competence_modal: true, selected_competence: competence });
    };

    handleEditLineCompetence = (competence: CompetenceShortData) => {
        this.setState({ selected_competence: competence, show_create_edit_competence_modal: true });
    };

    handleDeleteCompetence = async (competence: CompetenceShortData | null) => {
        if (competence === null) {
            return;
        }
        this.setState({ loading_child_lines: true, message: { msg: "", type: "no-data" } });
        const updated_competences = this.state.child_competences.filter(c => c.id !== competence.id);
        const updated_competence_req = updated_competences.map(c => ({
            competence_id: c.id,
            factor: c.factor,
        }));
        try {
            await getBackend().manufacturing.updateLine({
                line_uuid: this.props.selected.uuid,
                competences: updated_competence_req,
                external_id: this.props.selected.external_id,
                tags: this.props.selected.tags,
                title: this.props.selected.title,
                weight: this.props.selected.weight,
            });

            this.setState({ child_competences: updated_competences });
        } catch (error) {
            this.setState({
                message: {
                    msg: translate("common.error_save", "An error occurred while saving data"),
                    type: "error",
                },
            });
        } finally {
            this.setState({
                show_delete_competence_modal: false,
                selected_competence: null,
                loading_child_lines: false,
            });
        }
    };

    handleSaveLineCompetence = async (competence: CompetenceShortData) => {
        let updated_competences: CompetenceShortData[] = [];
        const index = this.state.child_competences.findIndex(c => c.id === competence.id);

        this.setState({ loading_child_lines: true, message: { msg: "", type: "no-data" } });
        if (index >= 0) {
            updated_competences = [...this.state.child_competences];
            updated_competences[index] = competence;
        } else {
            // Don't send weight if user didn't set it
            if (competence.weight === -1) {
                delete (competence as any).weight;
            }
            updated_competences = [...this.state.child_competences, competence];
        }

        const updated_competences_req = updated_competences.map(c => ({
            competence_id: c.id,
            factor: c.factor,
            weight: c.weight,
        }));

        try {
            await getBackend().manufacturing.updateLine({
                line_uuid: this.props.selected.uuid,
                competences: updated_competences_req,
                external_id: this.props.selected.external_id,
                tags: this.props.selected.tags,
                title: this.props.selected.title,
                weight: this.props.selected.weight,
            });

            this.setState({
                child_competences: updated_competences,
            });
        } catch (error) {
            this.setState({
                message: {
                    msg: translate("common.error_save", "An error occurred while saving data"),
                    type: "error",
                },
            });
        } finally {
            this.setState({
                show_create_edit_competence_modal: false,
                selected_competence: null,
                loading_child_lines: false,
            });
        }
    };

    handleAddCompetence = () => {
        this.setState({
            show_create_edit_competence_modal: true,
            selected_competence: null,
        });
    };

    renderCreateEditLineCompetenceModal = () => {
        if (!this.hasEnabledChildLines()) return null;

        return (
            <LinesCreateEditCompetenceModal
                show={this.state.show_create_edit_competence_modal}
                onHide={() => this.setState({ selected_competence: null, show_create_edit_competence_modal: false })}
                onSave={this.handleSaveLineCompetence}
                child_line_data={this.state.selected_competence}
                child_competences={new Set(this.state.child_competences.map(c => c.id))}
                edit_link={this.props.editLink}
            />
        );
    };

    renderCategories = () => {
        return this.state.categories.map(category => (
            <React.Fragment key={category.name}>
                <span>{category.name}</span>
                <span className="ml-1 text-muted">
                    ({category.items.map(item => item.name).join(", ")})
                    <span className="ml-2" />
                </span>
            </React.Fragment>
        ));
    };

    handleTabSelect = (tab_key: string | null) => {
        if (tab_key === "status" || tab_key === "competent_people" || tab_key === "ekanban") {
            this.setState({ active_tab: tab_key });
        }
    };

    renderStatusTab = (has_enabled_competence_lines: boolean, can_edit: boolean, status: t.ILineStatusItem | null) => {
        return (
            <div className="mt-3">
                <div className="card-deck">
                    <div className="card">
                        <div className="card-header text-uppercase bg-transparent">
                            <FormattedMessage id="common.plants" defaultMessage="Plants" />
                        </div>
                        <div className="card-body">
                            <dl className="property-list">
                                <div>
                                    <dt>
                                        <FormattedMessage id="common.plant" defaultMessage="Plant" />
                                    </dt>
                                    <dd>{this.props.selected.plant_title}</dd>
                                </div>
                                <div>
                                    <dt>
                                        <FormattedMessage id="common.external_id" defaultMessage="External ID" />
                                    </dt>
                                    <dd>{this.props.selected.plant_external_id}</dd>
                                </div>
                                <div>
                                    <dt className="light">UUID</dt>
                                    <dd className="light">{this.props.selected.plant_uuid}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header text-uppercase bg-transparent">
                            <FormattedMessage id="common.linegroups" defaultMessage="Line-groups" />
                        </div>
                        <div className="card-body">
                            <dl className="property-list">
                                {this.state.lineGroups.map(group => (
                                    <div key={group.uuid}>
                                        <dt>
                                            <FormattedMessage id="common.linegroup" defaultMessage="Line-group" />
                                        </dt>
                                        <dd>{group.title}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </div>
                </div>
                {has_enabled_competence_lines && (
                    <div className="card-deck mt-3">
                        <div className="card">
                            <div className="card-header text-uppercase bg-transparent">
                                <FormattedMessage id="Manufacturing.Lines.child_lines" defaultMessage="Child lines" /> (
                                {!this.state.loading_child_lines ? this.state.child_competences.length : 0})
                                {can_edit && (
                                    <div className="float-right">
                                        <button
                                            className="btn btn-outline-secondary btn-short"
                                            onClick={this.handleAddCompetence}
                                            aria-label={translate("common.add", "Add")}
                                            title={translate("common.add", "Add")}
                                        >
                                            <i className="fas fa-plus mr-1" aria-hidden="true" />
                                            {translate("common.add", "Add")}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="list-group list-group-flush">
                                {!this.state.loading_child_lines &&
                                    this.state.child_competences.map(competence => {
                                        return (
                                            <div
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                                key={competence.id}
                                            >
                                                <div>
                                                    <div className="mb-1">{competence.title}</div>
                                                    <small>
                                                        {translate(
                                                            "ShiftAssignmentWeekPool.competence_factor",
                                                            "Competence factor"
                                                        )}
                                                        :&nbsp;
                                                        <span className="font-weight-bold">
                                                            {niceNumber(competence.factor, 2)}
                                                        </span>
                                                    </small>
                                                </div>
                                                {can_edit && (
                                                    <div>
                                                        <button
                                                            className="btn btn-outline-secondary btn-short px-1 ml-2"
                                                            onClick={() => this.handleEditLineCompetence(competence)}
                                                            aria-label={translate("common.edit", "Edit")}
                                                            title={translate("common.edit", "Edit")}
                                                        >
                                                            <i className="fas fa-edit" aria-hidden="true" />
                                                            <span className="sr-only">
                                                                <FormattedMessage
                                                                    id="common.edit"
                                                                    defaultMessage="Edit"
                                                                />
                                                            </span>
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger btn-short px-1 ml-2"
                                                            onClick={() => this.showDeleteCompetenceModal(competence)}
                                                            aria-label={translate("common.remove", "Remove")}
                                                            title={translate("common.remove", "Remove")}
                                                        >
                                                            <i className="fas fa-trash-alt" aria-hidden="true" />
                                                            <span className="sr-only">
                                                                <FormattedMessage
                                                                    id="common.remove"
                                                                    defaultMessage="Remove"
                                                                />
                                                            </span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                {this.state.loading_child_lines && (
                                    <div className="list-group-item">
                                        <Loader small />
                                    </div>
                                )}
                            </div>
                            <div className="space15"></div>
                        </div>
                    </div>
                )}
                <div className="d-flex">
                    <div className="order-first width100p mt-3">
                        <h5 className="pane-title">
                            <FormattedMessage id="common.status" defaultMessage="Status" />
                        </h5>
                    </div>
                </div>
                {status === null && (
                    <span>
                        <FormattedMessage id="common.not_available" defaultMessage="Not available" />
                    </span>
                )}
                {status !== null && <LineStatusDetails status={status} />}
                <div className="d-flex">
                    <div className="order-first width100p mt-3">
                        <h5 className="pane-title">
                            <FormattedMessage id="common.shifts" defaultMessage="Shifts" />
                        </h5>
                    </div>
                    {Auth.hasPermission(Auth.PERMISSION_NAMES.ElevatedPriviliges) &&
                        this.state.show_button_edit_calendar_line && (
                            <div className="card-container flex-row">
                                <FormattedMessage id="common.edit" defaultMessage="Edit" />
                                <div className="p-2">
                                    <Toggle
                                        checked={this.state.manual_calendar_line_edit}
                                        onChange={() => this.toggleManualCalendarLineEdit()}
                                    />
                                </div>
                            </div>
                        )}
                </div>
                {this.renderLineShifts()}
            </div>
        );
    };

    renderCompetentPeopleTab = () => {
        const { active_count, total_count } = this.getCompetentPeopleCounts();
        const displayed_competences = filterCompetencesByOnlyActive(
            this.state.line_competences,
            this.state.only_active_competent_people,
            this.state.person_active_by_uuid,
            this.state.person_active_enrichment_failed
        );

        return (
            <div className="card mt-3">
                <div className="card-header text-uppercase bg-transparent d-flex align-items-center flex-wrap lines-competent-people-header">
                    <span className="mr-auto">
                        <FormattedMessage id="Manufacturing.Lines.competent_people" defaultMessage="Competent people" />{" "}
                        ({this.formatCompetentPeopleCountLabel()})
                    </span>
                    <label className="lines-competent-people-header__only-active mb-0 d-flex align-items-center">
                        <span className="mr-2">
                            <FormattedMessage id="common.only_active" defaultMessage="Only active" />
                        </span>
                        <Toggle
                            checked={this.state.only_active_competent_people}
                            onChange={this.toggleOnlyActiveCompetentPeople}
                        />
                    </label>
                </div>
                <LinesCompetentPeople
                    competences={displayed_competences}
                    loading={this.state.loading_line_competences}
                    error={this.state.line_competences_error}
                    active_warning={this.state.line_competences_active_warning}
                    only_active={this.state.only_active_competent_people}
                    person_active_by_uuid={this.state.person_active_by_uuid}
                    person_active_enrichment_failed={this.state.person_active_enrichment_failed}
                    total_competence_count={total_count}
                    active_competence_count={active_count}
                />
                <div className="space15"></div>
            </div>
        );
    };

    renderEkanbanTab = (can_edit_kanban: boolean, kanban_link: string) => {
        return (
            <div className="mt-3">
                <div className="d-flex">
                    {can_edit_kanban && (
                        <div className="button-list order-last ml-auto">
                            <Link className="btn btn-outline-primary" to={kanban_link}>
                                <FormattedMessage id="common.edit" defaultMessage="Edit" />
                            </Link>
                        </div>
                    )}
                    <div className="order-first width100p">
                        <h5 className="pane-title">
                            <FormattedMessage id="Manufacturing.Lines.ekanban" defaultMessage="eKanBan" />
                        </h5>
                    </div>
                </div>
                <div className="order-first width100p">
                    <div className="white_box charts">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>
                                        <FormattedMessage id="common.material" defaultMessage="Material" />
                                    </th>
                                    <th>Min</th>
                                    <th>Max</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderMaterials()}</tbody>
                        </table>
                        {this.renderDashboardLinks()}
                    </div>
                </div>
            </div>
        );
    };

    render() {
        if (this.props.selected === null) return null;

        const can_edit = Auth.isInElevatedRole();
        const can_edit_kanban =
            can_edit || Auth.isInRole(Auth.ROLE_MAN_SHOPFLOOR_MANAGER) || Auth.isInRole(Auth.ROLE_MAN_PLANNER);

        const createLink = "/digital-twin/resources/line/create";
        const editLink = `${this.props.editLink}${this.props.selected.uuid}`;
        const kanbanLink = `${this.props.kanbanLink}${this.props.selected.uuid}`;
        let tags: React.ReactNode = "/";

        if (this.state.selected_line_tags !== undefined) {
            // generate tags
            tags = Object.keys(this.state.selected_line_tags)
                .sort((a, b) => a.localeCompare(b))
                .map((el, i) => renderTag(i, el, this.state.selected_line_tags[el]));
        }
        const status = this.state.status;
        const copy_line_state: Partial<LineCreateState> = {
            external_id: "",
            uuid: "",
            title: "",
            plant_uuid: this.props.selected.plant_uuid,
            tags: this.props.selected.tags,
            type: LINE_TAGS_ACCESS.type(this.props.selected.tags),
            weight: this.props.selected.weight,
            factor: String(this.props.selected.factor ?? 1),
            componentTitle: `${translate("Manufacturing.Lines.copy_line", "Copy line")} ${this.props.selected.title}`,
        };

        const has_enabled_competence_lines = this.hasEnabledChildLines();

        return (
            <div className="tab-content">
                <div className="tab-pane active tab-pane-text">
                    {this.state.message.msg && (
                        <ErrorComponent msg={this.state.message.msg} type={this.state.message.type} />
                    )}
                    <div className="d-flex">
                        {can_edit && (
                            <div className="button-list order-last ml-auto">
                                <Link className="btn btn-outline-primary" to={editLink}>
                                    <FormattedMessage id="common.edit" defaultMessage="Edit" />
                                </Link>
                                <Link
                                    className="btn btn-outline-primary"
                                    to={{
                                        pathname: createLink,
                                        state: copy_line_state,
                                    }}
                                >
                                    <FormattedMessage id="common.copy" defaultMessage="Copy" />
                                </Link>
                            </div>
                        )}

                        <div className="order-first">
                            <h5 className="pane-title">
                                <em>
                                    <FormattedMessage id="common.line" defaultMessage="Line" />
                                </em>
                                {this.props.selected.title}
                            </h5>
                        </div>
                    </div>

                    <dl className="property-list">
                        <div>
                            <dt>
                                <FormattedMessage id="common.external_id" defaultMessage="External ID" />
                            </dt>
                            <dd>
                                <div>{this.props.selected.external_id}</div>
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <FormattedMessage id="common.weight" defaultMessage="Weight" />
                            </dt>
                            <dd>
                                <div>{this.props.selected.weight}</div>
                            </dd>
                        </div>
                        <div>
                            <dt>{translate("Manufacturing.Lines.competence_factor", "Competence factor")}</dt>
                            <dd>
                                <div>{niceNumber(this.props.selected.factor, 2)}</div>
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <FormattedMessage id="common.categories" defaultMessage="Categories" />
                            </dt>
                            <dd>
                                <div>{this.renderCategories()}</div>
                            </dd>
                        </div>
                        <div>
                            <dt>
                                <FormattedMessage id="common.tags" defaultMessage="Tags" />
                            </dt>
                            <dd>{tags}</dd>
                        </div>
                        <div>
                            <dt className="light">
                                <FormattedMessage id="common.uuid" defaultMessage="UUID" />
                            </dt>
                            <dd className="light">
                                <div>{this.props.selected.uuid}</div>
                            </dd>
                        </div>
                    </dl>

                    <Tabs
                        id="line-details-tabs"
                        activeKey={this.state.active_tab}
                        onSelect={this.handleTabSelect}
                        className="mt-3"
                    >
                        <Tab eventKey="status" title={translate("common.status", "Status")}>
                            {this.renderStatusTab(has_enabled_competence_lines, can_edit, status)}
                        </Tab>
                        <Tab
                            eventKey="competent_people"
                            title={`${translate("Manufacturing.Lines.competent_people", "Competent people")} (${this.formatCompetentPeopleCountLabel()})`}
                        >
                            {this.renderCompetentPeopleTab()}
                        </Tab>
                        <Tab eventKey="ekanban" title={translate("Manufacturing.Lines.ekanban", "eKanBan")}>
                            {this.renderEkanbanTab(can_edit_kanban, kanbanLink)}
                        </Tab>
                    </Tabs>

                    {this.renderSaveCalendarLineModal()}
                    {this.renderCreateEditLineCompetenceModal()}
                    <ConfirmationModal
                        show={this.state.show_delete_competence_modal}
                        title={translate("common.delete", "Delete")}
                        accept_label={translate("common.delete", "Delete")}
                        cancel_label={translate("common.cancel", "Cancel")}
                        dangerous={true}
                        onAccept={() => this.handleDeleteCompetence(this.state.selected_competence)}
                        onCancel={() =>
                            this.setState({
                                show_delete_competence_modal: false,
                                selected_competence: null,
                            })
                        }
                    >
                        <FormattedMessage
                            id="common.are_you_sure_delete_competence"
                            defaultMessage="Are you sure you want to delete competence from line?"
                        />
                        <br />
                        {this.state.selected_competence && <strong>{this.state.selected_competence.title}</strong>}
                    </ConfirmationModal>
                </div>
            </div>
        );
    }
}

export default LinesDetails;
